import React, { useEffect, useState, useCallback } from "react";
import { Badge, Spin, Tooltip, Typography } from "antd";
import { ClockCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getOnDuty, type OnDutyEntry } from "../../utils/api/onDutyAPI";
import "./OnDuty.scss";

const { Title } = Typography;

const SHIFT_TYPE_LABEL: Record<string, string> = {
  DAILY_WAGE: "Dnevnica",
  TEMPORARY_HELP: "Ispomoć",
  HOURS: "Sati",
};

const getInitials = (first: string, last: string) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();

const formatTime = (iso?: string) => (iso ? dayjs(iso).format("HH:mm") : "");

const badgeClass = (type: string) => {
  if (type === "DAILY_WAGE") return "on-duty__badge on-duty__badge--dw";
  if (type === "TEMPORARY_HELP") return "on-duty__badge on-duty__badge--th";
  return "on-duty__badge on-duty__badge--hours";
};

const OnDutyCard: React.FC<{ entry: OnDutyEntry; soon?: boolean }> = ({ entry, soon }) => {
  const { t } = useTranslation();
  return (
    <div className={`on-duty__card${soon ? " on-duty__card--soon" : ""}`}>
      <div className="on-duty__card-top">
        <div
          className="on-duty__avatar"
          style={{ background: entry.color || "#64748b" }}
        >
          {getInitials(entry.firstName, entry.lastName)}
        </div>
        <div>
          <div className="on-duty__name">
            {entry.firstName} {entry.lastName}
          </div>
          {entry.locationName && (
            <div className="on-duty__location">{entry.locationName}</div>
          )}
        </div>
      </div>
      <div className="on-duty__meta">
        <span className={badgeClass(entry.shiftType)}>
          {SHIFT_TYPE_LABEL[entry.shiftType] || entry.shiftType}
        </span>
        {entry.shiftType === "HOURS" && entry.startTime && entry.endTime && (
          <span className="on-duty__time">
            <ClockCircleOutlined />
            {formatTime(entry.startTime)} – {formatTime(entry.endTime)}
          </span>
        )}
        {!soon && entry.shiftType === "HOURS" && entry.remainingHours != null && (
          <span className="on-duty__remaining">
            {t("Ostalo")}: {entry.remainingHours}h
          </span>
        )}
        {soon && entry.inMinutes != null && (
          <span className="on-duty__countdown">
            {entry.inMinutes < 60
              ? `${t("Za")} ${entry.inMinutes} min`
              : `${t("Za")} ${Math.round(entry.inMinutes / 60 * 10) / 10}h`}
          </span>
        )}
      </div>
    </div>
  );
};

const OnDuty: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId: string = typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const [onDuty, setOnDuty] = useState<OnDutyEntry[]>([]);
  const [startingSoon, setStartingSoon] = useState<OnDutyEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await getOnDuty(clientId);
      setOnDuty(res.onDuty);
      setStartingSoon(res.startingSoon);
      setLastRefresh(new Date());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="on-duty">
      <div className="on-duty__header">
        <div className="on-duty__title">
          <Title level={4} style={{ margin: 0 }}>
            {t("Ko je na poslu")}
          </Title>
          <Badge count={onDuty.length} style={{ background: "#10b981" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {lastRefresh && (
            <span className="on-duty__refresh">
              {t("Ažurirano")}: {dayjs(lastRefresh).format("HH:mm:ss")}
            </span>
          )}
          <Tooltip title={t("Osvježi")}>
            <ReloadOutlined
              style={{ cursor: "pointer", color: "#64748b" }}
              onClick={fetchData}
              spin={loading}
            />
          </Tooltip>
        </div>
      </div>

      <Spin spinning={loading && onDuty.length === 0}>
        <p className="on-duty__section-title">
          <span style={{ color: "#10b981" }}>●</span>
          {t("Trenutno na poslu")} ({onDuty.length})
        </p>
        {onDuty.length === 0 ? (
          <div className="on-duty__empty">{t("Niko trenutno nije na poslu")}</div>
        ) : (
          <div className="on-duty__grid">
            {onDuty.map((e) => (
              <OnDutyCard key={`${e.userId}-${e.shiftType}`} entry={e} />
            ))}
          </div>
        )}

        {startingSoon.length > 0 && (
          <>
            <p className="on-duty__section-title">
              <span style={{ color: "#f59e0b" }}>●</span>
              {t("Počinje uskoro")} ({startingSoon.length})
            </p>
            <div className="on-duty__grid">
              {startingSoon.map((e) => (
                <OnDutyCard key={`soon-${e.userId}`} entry={e} soon />
              ))}
            </div>
          </>
        )}
      </Spin>
    </div>
  );
};

export default OnDuty;
